import { fromBase64, fromUtf8 } from '../vault/bytes';

/**
 * How long a recording is, from its structure rows: the `durationMs` of its
 * audio message. Nothing lighter carries it — not the front matter, not the
 * catalog, and the audio beside the note is bytes the web would have to fetch.
 *
 * The rows ride with the note in one of two places: a note rendered before the
 * structure moved out still ends in its base64 manifest; one rendered since
 * keeps them in `.manifests/<id>.jsonl` beside it, which costs a read.
 */

const AUDIO = 2;

interface Table {
  columns: string[];
  rows: unknown[][];
}

/** A cell as the app writes it: `{ t, v }`. */
function cell(value: unknown): unknown {
  return value && typeof value === 'object' && 'v' in value ? (value as { v: unknown }).v : undefined;
}

/** The longest audio in [table] that is the recording's own — not a copy
 *  another unit points back at, not a take that failed or was cancelled. */
function audioIn(table: Table | null): number | null {
  if (!table) return null;
  const type = table.columns.indexOf('type');
  const content = table.columns.indexOf('content');
  if (type < 0 || content < 0) return null;
  let longest: number | null = null;
  for (const row of table.rows) {
    if (Number(cell(row[type])) !== AUDIO) continue;
    const text = cell(row[content]);
    if (typeof text !== 'string') continue;
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      if (data['sourceAudioMessage'] != null || data['isCancelled'] === true || data['isFailed'] === true) continue;
      const ms = Number(data['durationMs']);
      if (ms > 0 && (longest === null || ms > longest)) longest = ms;
    } catch {
      // A payload that is not JSON names no length.
    }
  }
  return longest;
}

const INLINE = /<!--pondering:structure base64\n([\s\S]*?)\n-->/;

/** The length the note itself carries, in the manifest older renders end in. */
export function durationInNote(raw: string): number | null {
  const b64 = raw.match(INLINE)?.[1]?.trim();
  if (!b64) return null;
  try {
    const tables = (JSON.parse(fromUtf8(fromBase64(b64))) as { tables?: Record<string, unknown> }).tables;
    const messages = tables?.['messageData'] as { c?: string[]; r?: unknown[][] } | undefined;
    if (!messages?.c || !messages.r) return null;
    return audioIn({ columns: messages.c, rows: messages.r });
  } catch {
    return null;
  }
}

/** Where a note rendered since the structure moved out keeps its rows. */
export function rowsPathFor(id: string): string {
  return `.manifests/${id}.jsonl`;
}

/** The length in a `.manifests/<id>.jsonl` sidecar: a unit header, then each
 *  table's header naming its columns, then its rows, a line each. */
export function durationInRows(text: string): number | null {
  let current: Table | null = null;
  let messages: Table | null = null;
  for (const line of text.split('\n')) {
    if (!line.trim()) continue;
    try {
      const parsed = JSON.parse(line) as unknown;
      if (Array.isArray(parsed)) {
        current?.rows.push(parsed);
        continue;
      }
      const header = parsed as { t?: string; c?: string[] };
      current = header.t && header.c ? { columns: header.c, rows: [] } : null;
      if (header.t === 'messageData') messages = current;
    } catch {
      // A half-written line costs that line.
    }
  }
  return audioIn(messages);
}

/** As the app says it: `03:12`, minutes and seconds. */
export function durationLabel(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(Math.floor(seconds / 60))}:${two(seconds % 60)}`;
}
